import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Icon } from '@iconify/react';
import { VacancyScores } from 'types/vacancyStats';
import { formatCount, formatScore } from 'utils/vacancyStatsFormat';
import { EmptyState, Overline, WidgetCard } from './shared';

interface Props {
  scores: VacancyScores;
}

const ScoresTableWidget = ({ scores }: Props) => {
  const { evaluated, ofTotal, verdicts, top } = scores;

  return (
    <WidgetCard overline="Баллы" title="Оценки кандидатов" coverage={{ evaluated, ofTotal }}>
      {evaluated === 0 ? (
        <EmptyState />
      ) : (
        <Stack spacing={3}>
          {/* Вердикты — бейджами */}
          {verdicts.length > 0 && (
            <Box>
              <Overline>Вердикты</Overline>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {verdicts.map((verdict) => (
                  <Chip
                    key={verdict.label}
                    label={
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <span>{verdict.label}</span>
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: 'primary.dark' }}
                        >
                          {formatCount(verdict.count)}
                        </Box>
                      </Box>
                    }
                    sx={{
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      fontWeight: 600
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Топ кандидатов по баллу */}
          {top.length > 0 && (
            <Box>
              <Overline>Топ по баллу</Overline>
              <Box sx={{ overflowX: 'auto', ml: -2, width: 'calc(100% + 12px)' }}>
                <Table size="small" sx={{ minWidth: 420 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: 'text.disabled' }}>Кандидат</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.disabled' }}>Вердикт</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'text.disabled' }}>
                        Балл
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {top.map((candidate) => (
                      <TableRow key={candidate.candidateId} hover>
                        <TableCell sx={{ maxWidth: 240 }}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            {candidate.isFavorite && (
                              <Icon
                                icon="mdi:star"
                                width={16}
                                height={16}
                                color="#F4A918"
                                style={{ flexShrink: 0 }}
                              />
                            )}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                                noWrap
                                title={candidate.name || candidate.email}
                              >
                                {candidate.name || candidate.email}
                              </Typography>
                              {candidate.name && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  display="block"
                                  title={candidate.email}
                                >
                                  {candidate.email}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {candidate.label || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatScore(candidate.score)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </Stack>
      )}
    </WidgetCard>
  );
};

export default ScoresTableWidget;
